import { useEffect, useLayoutEffect } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { setTokenAccessor, setSessionEnder } from "@/shared/api/clients/tokenAccessor";
import { useSetOrgId } from "@/shared/auth/orgContext";

function decodeOrganizationId(token: string): string | null {
  const [, payload] = token.split(".");
  if (!payload) return null;

  const claims: unknown = JSON.parse(
    atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
  );
  if (typeof claims !== "object" || claims === null || !("org_id" in claims)) {
    return null;
  }
  return typeof claims.org_id === "string" && claims.org_id.length > 0
    ? claims.org_id
    : null;
}

export function AuthTokenBridge() {
  const { getAccessToken, signOut, user, organizationId } = useAuth();
  const userId = user?.id;
  const setOrganizationId = useSetOrgId();

  useLayoutEffect(() => {
    setTokenAccessor(getAccessToken);
    setSessionEnder(() => {
      signOut({ returnTo: `${window.location.origin}/login` });
    });
  }, [getAccessToken, signOut]);

  useEffect(() => {
    // A previous session's token can resolve after logout or a scope change.
    // Disable station queries until the current session's token is available.
    let cancelled = false;
    setOrganizationId(null);
    if (!userId) return;

    const resolveOrganization = async () => {
      try {
        const nextOrganizationId = decodeOrganizationId(await getAccessToken());
        if (!cancelled) setOrganizationId(nextOrganizationId);
      } catch {
        // Token retrieval or decoding can fail during auth recovery. Keep the
        // station scope empty; the auth layer owns session recovery.
        if (!cancelled) setOrganizationId(null);
      }
    };
    void resolveOrganization();

    return () => { cancelled = true; };
  }, [userId, organizationId, getAccessToken, setOrganizationId]);

  return null;
}
